import React, { MouseEventHandler, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { PermissionKey, QueryType } from '../../../utils/apollo/graphqlSchema';
import { SecondaryNavLinks } from '../../../components/SecondaryNav';
import { analysesVisitsSelector } from '../../../redux/selectors/analysesVisits';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { resetAnalysesVisits } from '../../../redux/slices/analysesVisits';
import { useReportNavLinks } from './utils';
import { useParams } from 'react-router-dom';
import {
  clearCreateNewReport,
  clearReplaceReport,
} from '../../../redux/slices/cachedEntities';
import ReportHistory from '../../../components/ReportHistory';
import useCachedEntitiesSelector from '../../../redux/selectorHooks/useCachedEntitiesSelector';
import useLoadAnalysesVisits from '../../../utils/analyses/useLoadAnalysesVisits';

const analysesVisitQueryTypes = [
  QueryType.Expression,
  QueryType.Funnel,
  QueryType.Path,
  QueryType.Prediction,
  QueryType.Retention,
  QueryType.DataTable,
  QueryType.Significance,
];
const analysesVisitPermissionKeys = [PermissionKey.AnalysisRead];

const Container = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 16px;
  height: 100%;
  width: 100%;
  padding-top: 12px;
`;
const FixedSection = styled.div`
  padding-right: 12px;
  padding-left: 12px;
`;

const ReportNav = () => {
  const dispatch = useAppDispatch();
  const urlParams = useParams();
  const builderId = urlParams?.builderId;

  const analysesVisitsList = useAppSelector(analysesVisitsSelector);
  const createNewReport = useCachedEntitiesSelector('createNewReport');
  const replaceReport = useCachedEntitiesSelector('replaceReport');

  // Load analyses visits to populate History component
  const visitsLoading = useLoadAnalysesVisits(
    analysesVisitQueryTypes,
    analysesVisitPermissionKeys,
  );

  const navLinks = useReportNavLinks();

  const handleLinkClick = () => {
    if (replaceReport) {
      dispatch(clearReplaceReport());
    }

    if (createNewReport) {
      dispatch(clearCreateNewReport());
    }
  };

  const onReportHistoryLinkClick: MouseEventHandler<HTMLAnchorElement> = (
    event,
  ) => {
    // defaultPrevented check is added to avoid running this code
    // when user opens Info popover
    if (!event.defaultPrevented) {
      handleLinkClick();
    }
  };

  const visitedReports = useMemo(() => {
    if (analysesVisitsList?.length > 0) {
      // All other than Cohort analyses
      return analysesVisitsList.filter(
        (analysisVisit) => analysisVisit.queryType !== QueryType.CohortSample,
      );
    }

    return [];
  }, [analysesVisitsList]);

  // Perform clean-up on unmount
  useEffect(() => {
    return () => {
      dispatch(resetAnalysesVisits());
    };
  }, [dispatch]);

  return (
    <Container>
      <FixedSection>
        <SecondaryNavLinks links={navLinks} onClick={handleLinkClick} />
      </FixedSection>
      {!visitsLoading && visitedReports.length > 0 && (
        <ReportHistory
          heading={'History'}
          visits={visitedReports}
          onLinkClick={onReportHistoryLinkClick}
          selectedAnalysisId={builderId}
        />
      )}
    </Container>
  );
};

export default ReportNav;
